import api from '@/libs/axiosInstance';
import type { ApiResponse, McpConsentResponse, McpConnectionsResponse } from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';

export const mcpQueryKeys = {
  consent: (transaction: string | null) => ['mcp', 'consent', transaction] as const,
  connections: ['mcp', 'connections'] as const,
  // Under `connections` on purpose: reducing or revoking a connection invalidates its activity too.
  connectionActivity: (id: string) => ['mcp', 'connections', id, 'activity'] as const,
};

export function useMcpConsentQuery(transaction: string | null) {
  return useQuery({
    queryKey: mcpQueryKeys.consent(transaction),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<McpConsentResponse>>(
        `/mcp/oauth/transactions/${transaction}`,
        { withCredentials: true }
      );
      if (!data.data) throw new Error('Missing consent response');
      return data.data;
    },
    enabled: Boolean(transaction),
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });
}

/**
 * `refetchInterval` lets a screen waiting for a brand to finish connecting an assistant in
 * another tab notice the new connection without a reload.
 */
export function useMcpConnectionsQuery(options: { refetchInterval?: number | false } = {}) {
  return useQuery({
    refetchInterval: options.refetchInterval ?? false,
    queryKey: mcpQueryKeys.connections,
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<McpConnectionsResponse>>(
        '/account/connected-assistants',
        { withCredentials: true }
      );
      if (!data.data) throw new Error('Missing connections response');
      return data.data;
    },
    retry: false,
  });
}
